import { getCompanyTimezone } from "@repo/app-types/entities/company";
import type { CopilotProposalId } from "@repo/app-types/entities/copilotProposal";
import type { ToolSettings } from "@repo/app-types/entities/tool";
import {
  buildTestCodeToolInputContext,
  type CodeTool,
  type CodeToolRunReport,
} from "@repo/app-types/entities/tools/code";
import type { CustomToolParameter } from "@repo/app-types/entities/tools/custom";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { FieldDescription } from "@repo/ui/field/fieldDescription";
import { FieldError } from "@repo/ui/field/fieldError";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { IconPlayerPlay } from "@tabler/icons-react";
import { useId, useState, type FC } from "react";
import { twMerge } from "tailwind-merge";
import { useTestToolMutation } from "../../api/entities/tool.js";
import { getApiErrorMessage } from "../../api/helpers/errors.js";
import { useAppSelector } from "../../hooks/hook.js";
import { PH_MASK_CLASS } from "../analytics/sessionRecordingMasking.js";
import { selectCompany } from "../company/companySlice.js";
import { CodeToolRunDetails } from "./components/codeToolRunDetails.js";
import { LazyCodeEditor } from "./components/lazyCodeEditor.js";
import type { PreparedToolSettings } from "./hooks/useBaseToolForm.js";

const formErrorsMessage = "Fix the errors above to run a test.";

/** A copilot proposal's test input, as JSON text. */
export interface ProposedTestInput {
  proposalId: CopilotProposalId;
  input: string;
}

interface CodeToolTestPanelProps {
  tool: CodeTool;
  /** The form's parameters now, saved or not. */
  parameters: readonly CustomToolParameter[];
  /**
   * The form's unsaved settings and secret changes, or `undefined` when they
   * aren't valid yet (the form then shows why).
   */
  prepareSettings: () => PreparedToolSettings<ToolSettings> | undefined;
  /** Shows a refusal on the form's fields; whether any field took it. */
  applySaveError: (error: unknown) => boolean;
  /**
   * The linked copilot proposal's input. Each new proposal replaces the input
   * once; when the proposal goes away, the starter input comes back.
   */
  proposedInput?: ProposedTestInput;
}

/**
 * Runs the tool live as the form has it now, secrets included, on an input the
 * tester writes.
 */
export const CodeToolTestPanel: FC<CodeToolTestPanelProps> = (props) => {
  const { tool, parameters, prepareSettings, applySaveError, proposedInput } =
    props;

  const labelId = useId();
  const descriptionId = useId();
  const inputErrorId = useId();

  // Follows the form's parameters until the tester edits it.
  const [editedInput, setEditedInput] = useState<string | undefined>(undefined);
  const company = useAppSelector(selectCompany);
  const inputText =
    editedInput ?? buildStarterInput(parameters, getCompanyTimezone(company));
  const [inputError, setInputError] = useState<string | undefined>(undefined);
  // A new proposal's input replaces the editor's once; later edits stand.
  // When the proposal goes away, so does its input. Set while rendering, so
  // the new input is what the first render shows.
  const proposalId = proposedInput?.proposalId;
  const [lastProposalId, setLastProposalId] = useState<
    CopilotProposalId | undefined
  >(undefined);
  if (proposalId !== lastProposalId) {
    setLastProposalId(proposalId);
    setEditedInput(proposedInput?.input);
    setInputError(undefined);
  }
  const [runError, setRunError] = useState<string | undefined>(undefined);

  // The request carries secret values, so the mutation is reset as soon as it
  // settles rather than leaving its arguments in the store; what it answered
  // is kept here instead.
  const [testTool, { isLoading, reset }] = useTestToolMutation();
  const [result, setResult] = useState<CodeToolRunReport | undefined>(
    undefined,
  );

  const handleRun = async (): Promise<void> => {
    setResult(undefined);
    setRunError(undefined);
    const input = parseTestInput(inputText);
    if (typeof input === "string") {
      setInputError(input);
      return;
    }

    const prepared = prepareSettings();
    if (!prepared) {
      setRunError(formErrorsMessage);
      return;
    }

    try {
      const testResult = await testTool({
        toolId: tool.id,
        input,
        settings: prepared.settings,
        secrets: prepared.secrets,
      }).unwrap();
      setResult(testResult);
    } catch (error) {
      // A refusal about the form, like code that doesn't compile, shows on
      // its field as it would on save.
      setRunError(
        applySaveError(error)
          ? formErrorsMessage
          : getApiErrorMessage(error, "The test run failed. Please try again."),
      );
    } finally {
      reset();
    }
  };

  return (
    <div className="w-full rounded-lg border border-border-light bg-white">
      <div className="border-b border-border-light p-4">
        <Label size={Size.MEDIUM}>Test run</Label>
        <p className="mt-1 text-xs text-muted-foreground">
          Runs the tool for real with what&apos;s in the form, including unsaved
          changes. Requests are sent with your secrets. Nothing is saved.
        </p>
      </div>

      <div className="flex flex-col gap-3 p-4">
        <div>
          <div
            id={labelId}
            className="text-sm leading-6 font-semibold text-foreground"
          >
            Input
          </div>
          <div className="mt-2">
            <LazyCodeEditor
              language="json"
              formatOnBlur
              minHeight={120}
              value={inputText}
              onChange={(value) => {
                setEditedInput(value);
                setInputError(undefined);
              }}
              labelId={labelId}
              describedBy={`${descriptionId} ${inputErrorId}`}
              hasError={inputError !== undefined}
            />
          </div>
          {inputError ? (
            <FieldError id={inputErrorId}>{inputError}</FieldError>
          ) : (
            <FieldDescription id={descriptionId}>
              What your script receives as <code>input</code>. Fill in{" "}
              <code>params</code> with what the agent would pass. The rest are
              test placeholders for an inbound call; edit them to try other
              cases.
            </FieldDescription>
          )}
        </div>

        <div>
          <Button
            variant={ButtonVariant.Secondary}
            size={Size.MEDIUM}
            icon={<IconPlayerPlay className="h-4 w-4" />}
            onClick={handleRun}
            isLoading={isLoading}
            isDisabled={isLoading}
          >
            Run test
          </Button>
          {runError ? <FieldError>{runError}</FieldError> : null}
        </div>

        {result ? <CodeToolRunReportView result={result} /> : null}
      </div>
    </div>
  );
};

interface CodeToolRunReportViewProps {
  result: CodeToolRunReport;
}

const CodeToolRunReportView: FC<CodeToolRunReportViewProps> = (props) => {
  const { result } = props;

  return (
    <div
      className={twMerge(
        "space-y-3 border-t border-border-light pt-3 text-xs text-foreground",
        PH_MASK_CLASS,
      )}
    >
      <p role="status" className="text-muted-foreground">
        {result.ok ? "Succeeded" : "Failed"} in {result.duration_ms.toFixed(0)}{" "}
        ms
      </p>

      {result.ok ? (
        <TestResultDetail
          label="Result"
          value={
            result.result === undefined
              ? "(nothing returned)"
              : JSON.stringify(result.result, null, 2)
          }
        />
      ) : (
        <TestResultDetail
          label="Error"
          value={[
            `${result.error.code}: ${result.error.message}`,
            result.error.stack,
          ]
            .filter((line) => line !== undefined)
            .join("\n")}
          isError
        />
      )}

      <CodeToolRunDetails requests={result.requests} logs={result.logs} />
    </div>
  );
};

interface TestResultDetailProps {
  label: string;
  value: string;
  isError?: boolean;
}

const TestResultDetail: FC<TestResultDetailProps> = (props) => {
  const { label, value, isError } = props;
  return (
    <div>
      <div className="mb-1 font-medium text-muted-foreground">{label}</div>
      <pre
        className={twMerge(
          "overflow-x-auto rounded bg-gray-50 p-2 font-mono text-[11px] break-all whitespace-pre-wrap",
          isError && "bg-red-50 text-red-800",
        )}
      >
        {value}
      </pre>
    </div>
  );
};

/**
 * The script's whole input with test placeholders, and each of the tool's
 * parameters for the tester to fill in.
 */
function buildStarterInput(
  parameters: readonly CustomToolParameter[],
  companyTimezone: string,
): string {
  const params = Object.fromEntries(
    parameters
      .filter(({ name }) => name !== "")
      .map(({ name }) => [name, null]),
  );
  const input = { params, ...buildTestCodeToolInputContext(companyTimezone) };
  return JSON.stringify(input, null, 2);
}

/** The input as an object, or what is wrong with it. */
function parseTestInput(text: string): object | string {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return "Enter valid JSON.";
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return "Enter a JSON object.";
  }
  return parsed;
}
